import React, { useState, useEffect, useMemo, useRef } from 'react'
import { supabase } from './supabaseClient'
import { useAuth } from './AuthContext'
import { lerTudo } from './lerTudo'
import FechoAno from './FechoAno'
import { C } from './theme'
import { CopyButton } from './CopyButton'
import ImportarSage from './ImportarSage'
import {
  TrendingUp, Download, RefreshCw, AlertTriangle, Info, Package, Calendar, Store, Plus,
} from 'lucide-react'

// ─────────────────────────────────────────────────────────────────────────────
// Previsão de facturação — para levar ao contabilista.
//
// REGRA QUE MANDA EM TUDO: só entram valores SEM IVA. É o que é tributado.
//   B2B         → encomendas.total_sem_iva − desconto_valor  (já s/IVA na origem)
//   Marketplace → vendas_marketplace.valor_faturado          (lançado s/IVA)
//
// Esta aba NÃO reaproveita nada do Capital. Os valores de lá têm problemas
// conhecidos por resolver, e uma previsão que herdasse esses erros seria pior
// do que não existir — porque ia parecer certa.
//
// TRÊS CAMADAS, NUNCA MISTURADAS. Misturar o que é certo com o que é palpite é
// a forma mais fácil de dar um número que ninguém devia usar:
//   1. Facturado          — factura emitida. Facto.
//   2. Contratado         — encomenda paga/promovida, factura por emitir. Quase facto.
//   3. Projectado         — meses que faltam. Banda, nunca um número só.
//
// A conta interna fica FORA por defeito: não é facturada a terceiros, é
// mercadoria que só vira receita quando sai no marketplace. Contá-la aqui era
// contar a mesma venda duas vezes.
// ─────────────────────────────────────────────────────────────────────────────

const LS_LIMITE = 'previsao_limite_regime'
const LS_FILTROS = 'previsao_filtros_v1'
const LIMITE_DEFEITO = 200000

const fmt = (n) => Number(n || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmt0 = (n) => Math.round(Number(n || 0)).toLocaleString('pt-PT')
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function csvEscape(v) {
  const s = String(v ?? '')
  return (s.includes(';') || s.includes('"') || s.includes('\n')) ? '"' + s.replace(/"/g, '""') + '"' : s
}

export default function PrevisaoFaturacao() {
  const { isAdmin } = useAuth()

  const [encomendas, setEncomendas] = useState([])
  const [marketplace, setMarketplace] = useState([])
  const [internosIds, setInternosIds] = useState(new Set())
  const [declarada, setDeclarada] = useState([])   // facturacao real importada do Sage
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState('')

  const guardados = (() => {
    try { return JSON.parse(localStorage.getItem(LS_FILTROS) || '{}') } catch { return {} }
  })()
  const [aba, setAba] = useState(guardados.aba || 'facturacao')
  const [ano, setAno] = useState(guardados.ano || new Date().getFullYear())
  const [incluirInterno, setIncluirInterno] = useState(guardados.incluirInterno ?? false)
  const [incluirMarketplace, setIncluirMarketplace] = useState(guardados.incluirMarketplace ?? true)
  const [limite, setLimite] = useState(() => {
    const g = localStorage.getItem(LS_LIMITE)
    return g != null && g !== '' ? Number(g) : LIMITE_DEFEITO
  })

  useEffect(() => {
    localStorage.setItem(LS_FILTROS, JSON.stringify({ aba, ano, incluirInterno, incluirMarketplace }))
  }, [aba, ano, incluirInterno, incluirMarketplace])
  useEffect(() => { localStorage.setItem(LS_LIMITE, String(limite)) }, [limite])

  const carregar = async () => {
    setLoading(true); setErro('')
    try {
      const [encRes, mktRes, perfRes, decRes] = await Promise.all([
        supabase.from('encomendas')
          .select('id, numero_encomenda, cliente_id, cliente_nome, created_at, estado, total_sem_iva, desconto_valor, fatura_emitida_em, fatura_numero, pago_1a_prestacao, promovido_pedido_em, historica, origem')
          .order('created_at'),
        supabase.from('vendas_marketplace')
          .select('id, tipo, produto_nome, qtd, valor_total, valor_faturado, marketplace, data, data_recebimento, recebido_em, fatura_numero, historica, created_at')
          .order('data'),
        supabase.from('perfis').select('id').eq('cliente_interno', true),
        supabase.from('faturacao_declarada').select('*').order('ano').order('mes'),
      ])
      // AS QUATRO SAO VERIFICADAS, e nao so duas. Uma leitura bloqueada pela RLS
      // nao da excepcao: devolve zero linhas. Com `perfis` a falhar em silencio
      // o ecra concluia que nao ha contas internas e passava a facturacao da
      // Catia a receita a serio — e o numero que vai ao contabilista, e o que
      // conta para o limite do regime, ficava inflado sem um aviso.
      if (encRes.error) throw new Error('Encomendas: ' + encRes.error.message)
      if (mktRes.error) throw new Error('Marketplace: ' + mktRes.error.message)
      if (perfRes.error) throw new Error('Contas internas: ' + perfRes.error.message)
      if (decRes.error) throw new Error('Facturacao declarada (Sage): ' + decRes.error.message)
      setEncomendas(encRes.data || [])
      setMarketplace(mktRes.data || [])
      setInternosIds(new Set((perfRes.data || []).map(p => p.id)))
      setDeclarada(decRes.data || [])
    } catch (e) {
      setErro(e.message || String(e))
    }
    setLoading(false)
  }
  useEffect(() => { if (isAdmin) carregar() }, [isAdmin])

  const dados = useMemo(() => {
    const hoje = new Date()
    const anoActual = hoje.getFullYear()
    const mesActual = hoje.getMonth()   // 0-11

    // ── B2B ────────────────────────────────────────────────────────────────
    // Excluídas: canceladas, históricas (registos pré-sistema) e — salvo opção
    // em contrário — a conta interna.
    const linhasB2B = encomendas.filter(e => {
      if (e.historica) return false
      if (e.estado === 'cancelado') return false
      if (new Date(e.created_at).getFullYear() !== Number(ano)) return false
      if (!incluirInterno && internosIds.has(e.cliente_id)) return false
      return true
    }).map(e => ({
      mes: new Date(e.created_at).getMonth(),
      base: Number(e.total_sem_iva || 0) - Number(e.desconto_valor || 0),
      facturada: !!e.fatura_emitida_em,
      // "Contratado" = já há compromisso: o cliente pagou, ou o admin promoveu.
      contratada: !!e.pago_1a_prestacao || !!e.promovido_pedido_em,
      interna: internosIds.has(e.cliente_id),
      numero: e.numero_encomenda,
      nome: e.cliente_nome,
    }))

    // ── Marketplace ────────────────────────────────────────────────────────
    const linhasMkt = (incluirMarketplace ? marketplace : []).filter(v => {
      if (v.historica) return false
      if (!v.data) return false
      return new Date(v.data).getFullYear() === Number(ano)
    }).map(v => ({
      mes: new Date(v.data).getMonth(),
      base: Number(v.valor_faturado ?? v.valor_total ?? 0),
      semValorFaturado: v.valor_faturado == null,
      produto: v.produto_nome,
      marketplace: v.marketplace,
      servico: v.tipo === 'servico',
    }))

    const soma = (arr, f = () => true) => arr.filter(f).reduce((s, l) => s + l.base, 0)

    // ── Facturação real (Sage) ─────────────────────────────────────────────
    // Nos meses que o Sage cobre manda o Sage: ele tem tudo o que foi facturado,
    // a plataforma só conhece o que passou por ela. Nos outros meses vale o que
    // a plataforma sabe.
    const decPorMes = new Map(
      declarada.filter(d => d.ano === Number(ano))
               .map(d => [d.mes - 1, Number(d.valor_sem_iva || 0)])
    )

    const b2bFacturadoPlat = soma(linhasB2B, l => l.facturada)
    const mktFacturado = soma(linhasMkt)
    // Mercadoria e servicos somam para o mesmo total, mas o contabilista precisa
    // de os ver separados: o regime simplificado aplica coeficientes diferentes
    // a cada um, e a mesma facturacao da rendimentos tributaveis diferentes
    // conforme o corte.
    const mktProdutos = soma(linhasMkt, l => !l.servico)
    const mktServicos = soma(linhasMkt, l => l.servico)

    const facturadoPorMes = Array.from({ length: 12 }, (_, m) => decPorMes.has(m)
      ? decPorMes.get(m)
      : soma(linhasB2B, l => l.mes === m && l.facturada) + soma(linhasMkt, l => l.mes === m))
    const facturado = facturadoPorMes.reduce((a, b) => a + b, 0)

    // O "contratado" nunca vem do Sage: por definição ainda não foi facturado,
    // logo não pode lá estar. Os dois conjuntos não se tocam.
    const b2bContratado = soma(linhasB2B, l => !l.facturada && l.contratada)
    // O QUE PODE ESTAR CONTADO DUAS VEZES.
    //
    // "Contratado" sao encomendas que a PLATAFORMA nao sabe que foram
    // facturadas — e `fatura_emitida_em` e preenchido a mao no Financeiro. Se
    // uma encomenda foi facturada no Sage e ninguem a marcou aqui, ela entra no
    // total do mes vindo do Sage E entra outra vez no contratado.
    //
    // Nao se corrige sozinho porque nao ha como saber qual das duas esta certa.
    // Mede-se e diz-se: e o valor que esta em risco, num mes que o Sage cobre.
    const contratadoEmMesDoSage = soma(linhasB2B,
      l => !l.facturada && l.contratada && decPorMes.has(l.mes))
    const nContratadoEmMesDoSage = linhasB2B.filter(
      l => !l.facturada && l.contratada && decPorMes.has(l.mes)).length
    const b2bSemCompromisso = soma(linhasB2B, l => !l.facturada && !l.contratada)

    const realizado = facturado + b2bContratado
    const b2bFacturado = b2bFacturadoPlat

    // ── Meses fechados, para a projecção ───────────────────────────────────
    // Só entram meses COMPLETOS. Incluir o mês a decorrer puxa a média para
    // baixo e faz a projecção parecer pior do que é.
    const porMes = Array.from({ length: 12 }, (_, m) => {
      const temSage = decPorMes.has(m)
      return {
        mes: m,
        temSage,
        b2b: soma(linhasB2B, l => l.mes === m),
        mkt: soma(linhasMkt, l => l.mes === m),
        // O mês vale o que o Sage diz quando o Sage o cobre. É esse valor que
        // alimenta a média da projecção — usar o da plataforma subestimaria.
        total: temSage ? decPorMes.get(m)
                       : soma(linhasB2B, l => l.mes === m) + soma(linhasMkt, l => l.mes === m),
      }
    })

    const ehAnoCorrente = Number(ano) === anoActual
    // Fracção do mês corrente já decorrida
    const diasNoMes = new Date(anoActual, mesActual + 1, 0).getDate()
    const decorridoDoMes = ehAnoCorrente ? hoje.getDate() / diasNoMes : 1

    // Um mês conta para a média se acabou — ou se já vai tão adiantado que o
    // valor dele é praticamente o final. Excluir um mês a 97% seria deitar fora
    // o dado mais recente e mais relevante só por formalidade, e isso torna a
    // projecção artificialmente pessimista.
    const MES_QUASE_FECHADO = 0.85
    const correnteConta = ehAnoCorrente && decorridoDoMes >= MES_QUASE_FECHADO
    const ultimoMesFechado = ehAnoCorrente ? (correnteConta ? mesActual : mesActual - 1) : 11
    // Um mes a zero DEPOIS de a actividade comecar e informacao, nao ruido:
    // deitar fora um mes mau puxa a media para cima e faz a projeccao prometer
    // o que nao ha. So se ignoram os meses anteriores a primeira facturacao,
    // que sao zero por a empresa ainda nao estar a vender.
    const primeiroComValor = porMes.findIndex(m => m.total > 0)
    const mesesFechados = primeiroComValor < 0 ? [] : porMes
      .filter(m => m.mes <= ultimoMesFechado && m.mes >= primeiroComValor)
    // Meses a zero no meio da actividade: pode ser um mes mau a serio ou dados
    // por importar. A diferenca muda a projeccao toda, e so o Marco sabe qual e.
    const mesesVaziosNoMeio = mesesFechados.filter(m => m.total === 0).map(m => m.mes)
    const mesesRestantes = ehAnoCorrente ? (11 - mesActual) + (correnteConta ? 0 : 1) : 0

    const valores = mesesFechados.map(m => m.total)
    const media = valores.length ? valores.reduce((a, b) => a + b, 0) / valores.length : 0
    const minimo = valores.length ? Math.min(...valores) : 0
    const maximo = valores.length ? Math.max(...valores) : 0

    // Se o mês corrente já conta como fechado, o que falta são só meses inteiros.
    // Senão, falta o resto deste mês mais os inteiros que vêm a seguir.
    const feitoNoMesCorrente = ehAnoCorrente ? porMes[mesActual].total : 0
    const fraccaoMesRestante = correnteConta ? 0 : Math.max(0, 1 - decorridoDoMes)
    const mesesInteirosRestantes = correnteConta
      ? mesesRestantes
      : Math.max(0, mesesRestantes - 1)

    const projeccao = (valorMes) => valorMes * mesesInteirosRestantes + valorMes * fraccaoMesRestante

    const projPessimista = projeccao(minimo)
    const projCentral = projeccao(media)
    const projOptimista = projeccao(maximo)

    const totalPessimista = realizado + projPessimista
    const totalCentral = realizado + projCentral
    const totalOptimista = realizado + projOptimista

    // Qualidade dos dados — dito, não escondido
    const mktSemValorFaturado = linhasMkt.filter(l => l.semValorFaturado).length
    const b2bPorFacturar = linhasB2B.filter(l => !l.facturada && l.contratada).length
    // Vendas de marketplace marcadas como históricas ficam de fora. A marca serve
    // para stock pré-sistema, mas a VENDA pode ter acontecido este ano e ser
    // receita a declarar. Mostrar 0 € sem explicar levava a pensar que não houve
    // vendas nenhumas — e não é isso que os dados dizem.
    const mktHistoricasNoAno = marketplace.filter(v =>
      v.historica && v.data && new Date(v.data).getFullYear() === Number(ano)
    )
    const mktHistoricasValor = mktHistoricasNoAno.reduce(
      (s, v) => s + Number(v.valor_faturado ?? v.valor_total ?? 0), 0)

    return {
      linhasB2B, linhasMkt, porMes, mesesFechados, mesesRestantes,
      b2bFacturado, b2bContratado, b2bSemCompromisso, mktFacturado, mktProdutos, mktServicos, realizado,
      media, minimo, maximo, feitoNoMesCorrente,
      projPessimista, projCentral, projOptimista,
      totalPessimista, totalCentral, totalOptimista,
      mktSemValorFaturado, b2bPorFacturar, ehAnoCorrente,
      facturado, decPorMes, nMesesSage: decPorMes.size,
      mktHistoricasNoAno: mktHistoricasNoAno.length, mktHistoricasValor,
      contratadoEmMesDoSage, nContratadoEmMesDoSage, mesesVaziosNoMeio,
      correnteConta,
      nMesesFechados: mesesFechados.length,
    }
  }, [encomendas, marketplace, internosIds, declarada, ano, incluirInterno, incluirMarketplace])

  const exportarCSV = () => {
    const linhas = [
      ['PREVISAO DE FACTURACAO', String(ano)],
      ['Todos os valores SEM IVA'],
      [''],
      ['CAMADA', 'VALOR EUR', 'NATUREZA'],
      // As tres linhas tem de somar. Antes a primeira vinha so da plataforma e a
      // ultima ja contava com o Sage, portanto assim que houvesse um mes
      // importado as contas do documento deixavam de fechar — e este e o
      // documento que vai para o contabilista.
      ['Facturado', dados.facturado.toFixed(2),
        dados.nMesesSage > 0 ? dados.nMesesSage + ' mes(es) do Sage, restantes da plataforma' : 'factura emitida'],
      ['Contratado por facturar', dados.b2bContratado.toFixed(2), 'encomenda paga ou promovida'],
      ['REALIZADO ATE AGORA', dados.realizado.toFixed(2), 'facturado + contratado'],
      [''],
      ['Do facturado, pela plataforma:', '', ''],
      ['  B2B', dados.b2bFacturado.toFixed(2), dados.nMesesSage > 0 ? 'so os meses sem Sage' : ''],
      ['  Marketplaces (mercadoria)', dados.mktProdutos.toFixed(2), ''],
      ['  Servicos prestados', dados.mktServicos.toFixed(2), 'coeficiente fiscal diferente do da mercadoria'],
      [''],
      ['PROJECCAO MESES RESTANTES', '', `${dados.mesesRestantes} mes(es)`],
      ['Pessimista', dados.projPessimista.toFixed(2), 'ao ritmo do pior mes fechado'],
      ['Central', dados.projCentral.toFixed(2), 'ao ritmo da media'],
      ['Optimista', dados.projOptimista.toFixed(2), 'ao ritmo do melhor mes fechado'],
      [''],
      ['TOTAL ESTIMADO PESSIMISTA', dados.totalPessimista.toFixed(2), ''],
      ['TOTAL ESTIMADO CENTRAL', dados.totalCentral.toFixed(2), ''],
      ['TOTAL ESTIMADO OPTIMISTA', dados.totalOptimista.toFixed(2), ''],
      [''],
      ['Limite configurado', limite.toFixed(2), ''],
      ['Conta interna incluida', incluirInterno ? 'sim' : 'nao', 'nao e facturada a terceiros'],
      ['Marketplace incluido', incluirMarketplace ? 'sim' : 'nao', ''],
      ['Meses fechados usados na media', String(dados.nMesesFechados), ''],
      ['Contratado em meses cobertos pelo Sage', dados.contratadoEmMesDoSage.toFixed(2),
        dados.nContratadoEmMesDoSage > 0 ? 'CONFERIR: pode estar contado duas vezes' : ''],
      [''],
      ['DETALHE POR MES', '', ''],
      ['Mes', 'B2B', 'Marketplace'],
      ...dados.porMes.map(m => [MESES[m.mes], m.b2b.toFixed(2), m.mkt.toFixed(2)]),
    ]
    const csv = '﻿' + linhas.map(l => l.map(csvEscape).join(';')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `previsao-facturacao-${ano}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const textoResumo = useMemo(() => [
    `PREVISÃO DE FACTURAÇÃO ${ano} — valores SEM IVA`,
    ``,
    `Já facturado:                ${fmt(dados.facturado)} €${dados.nMesesSage > 0 ? `  (${dados.nMesesSage} mês(es) do Sage)` : ''}`,
    `Contratado, por facturar:    ${fmt(dados.b2bContratado)} €`,
    `Realizado até agora:         ${fmt(dados.realizado)} €`,
    ``,
    `Projecção ${dados.mesesRestantes} mes(es) que faltam:`,
    `  pessimista:  ${fmt(dados.projPessimista)} €`,
    `  central:     ${fmt(dados.projCentral)} €`,
    `  optimista:   ${fmt(dados.projOptimista)} €`,
    ``,
    `TOTAL ESTIMADO ${ano}: entre ${fmt(dados.totalPessimista)} € e ${fmt(dados.totalOptimista)} €`,
    `Cenário central: ${fmt(dados.totalCentral)} €`,
    ``,
    `Limite de referência: ${fmt(limite)} € (${(dados.totalCentral / limite * 100).toFixed(0)}% no cenário central)`,
    `Conta interna ${incluirInterno ? 'INCLUÍDA' : 'excluída (não é facturada a terceiros)'}`,
    `Base: ${dados.nMesesFechados} mês(es) fechado(s)`,
    ...(dados.nContratadoEmMesDoSage > 0
      ? ['', `⚠ ${fmt(dados.contratadoEmMesDoSage)} € de "contratado" caem em meses que o Sage já cobre.`,
            `  Se essas facturas já foram emitidas e não estão marcadas na plataforma,`,
            `  estão contadas duas vezes. Confirmar antes de usar este número.`]
      : []),
  ].join('\n'), [dados, ano, limite, incluirInterno])

  if (!isAdmin) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
        <div style={{ textAlign: 'center', color: C.textMuted }}>
          <TrendingUp size={40} style={{ opacity: 0.4 }} />
          <p style={{ marginTop: '12px', fontSize: '13px' }}>Só o administrador vê esta página.</p>
        </div>
      </div>
    )
  }

  const pct = limite > 0 ? Math.min(100, dados.totalCentral / limite * 100) : 0
  const pctRealizado = limite > 0 ? Math.min(100, dados.realizado / limite * 100) : 0
  const corBarra = pct >= 100 ? C.danger : pct >= 80 ? C.warning : C.success

  const anosDisponiveis = [...new Set([
    ...encomendas.map(e => new Date(e.created_at).getFullYear()),
    new Date().getFullYear(),
  ])].sort((a, b) => b - a)

  return (
    // O <main> da app tem overflow:hidden — cada página traz o seu próprio
    // scroll. Sem isto a página ficava cortada em baixo e a roda do rato não
    // fazia nada, como se o conteúdo simplesmente não existisse.
    <div style={{ padding: '20px', maxWidth: '1100px', flex: 1, overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingUp size={20} color={C.primary} /> Previsão de Facturação
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: C.textMuted }}>
            Estimativa para levar ao contabilista. <strong>Todos os valores sem IVA</strong> — é o que é tributado.
          </p>
        </div>
        {aba === 'facturacao' && <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button onClick={carregar} disabled={loading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '7px 12px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, color: C.text, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
            <RefreshCw size={13} /> Actualizar
          </button>
          <button onClick={exportarCSV}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '7px 12px', border: 'none', borderRadius: '4px', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
            <Download size={13} /> Exportar CSV
          </button>
        </div>}
      </div>

      {erro && (
        <div style={{ padding: '10px 14px', background: C.dangerSoft, border: `1px solid ${C.danger}55`, borderRadius: '4px', color: C.danger, fontSize: '12px', marginBottom: '12px' }}>
          {erro}
        </div>
      )}

      {/* ── Filtros ── */}
      <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: '4px', marginBottom: '14px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.text, fontWeight: '600' }}>
          <Calendar size={13} color={C.textMuted} /> Ano
          <select value={ano} onChange={e => setAno(Number(e.target.value))}
            style={{ padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px' }}>
            {anosDisponiveis.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={incluirInterno} onChange={e => setIncluirInterno(e.target.checked)} style={{ cursor: 'pointer', accentColor: C.primary }} />
          Incluir conta interna
          <span title="A conta interna não é facturada a terceiros — é mercadoria que só vira receita quando sai no marketplace. Incluí-la conta a mesma venda duas vezes." style={{ display: 'inline-flex' }}>
            <Info size={12} color={C.textMuted} />
          </span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={incluirMarketplace} onChange={e => setIncluirMarketplace(e.target.checked)} style={{ cursor: 'pointer', accentColor: C.primary }} />
          Incluir marketplaces
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.text, fontWeight: '600', marginLeft: 'auto' }}>
          Limite de referência
          <input type="number" min="0" step="1000" value={limite} onChange={e => setLimite(Number(e.target.value) || 0)}
            style={{ width: '110px', padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', textAlign: 'right' }} />
          €
        </label>
      </div>

      {/* Dois separadores em vez de duas paginas: e a mesma conversa com o
          contabilista, vista pelos dois lados. O filtro do ano fica acima
          porque serve os dois; os das contas internas e do marketplace so
          fazem sentido no da facturacao. */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', borderBottom: `2px solid ${C.border}` }}>
        {[
          { id: 'facturacao', label: 'Facturação' },
          { id: 'fecho', label: 'Lucro e fecho do ano' },
        ].map(x => (
          <button key={x.id} onClick={() => setAba(x.id)}
            style={{
              padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: aba === x.id ? '800' : '600',
              color: aba === x.id ? C.text : C.textMuted,
              borderBottom: `2px solid ${aba === x.id ? C.primary : 'transparent'}`, marginBottom: '-2px',
            }}>
            {x.label}
          </button>
        ))}
      </div>

      {aba === 'fecho' ? (
        <FechoAno ano={ano} limite={limite} />
      ) : loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>A carregar…</div>
      ) : (
        <>
          {/* ── As três camadas ── */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '16px 18px', marginBottom: '14px' }}>
            <p style={{ margin: '0 0 12px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Facturação {ano} — sem IVA
            </p>

            <Camada cor={C.success} etiqueta="Facturado" nota={dados.nMesesSage > 0 ? 'facturas emitidas — do Sage' : 'factura emitida — facto'}
              valor={dados.facturado}
              detalhe={dados.nMesesSage > 0
                ? `${dados.nMesesSage} mês(es) do Sage · restantes da plataforma`
                : (dados.mktFacturado > 0
                    ? `B2B ${fmt(dados.b2bFacturado)} € · marketplaces ${fmt(dados.mktProdutos)} €`
                      + (dados.mktServicos > 0 ? ` · serviços ${fmt(dados.mktServicos)} €` : '')
                    : null)} />

            <Camada cor={C.info} etiqueta="Contratado, por facturar" nota="encomenda paga ou promovida — quase facto"
              valor={dados.b2bContratado}
              detalhe={dados.b2bPorFacturar > 0 ? `${dados.b2bPorFacturar} encomenda(s)` : null} />

            <div style={{ borderTop: `2px solid ${C.border}`, marginTop: '10px', paddingTop: '10px' }}>
              <Camada cor={C.text} etiqueta="Realizado até agora" valor={dados.realizado} destaque />
            </div>
          </div>

          {/* ── Projecção ── */}
          {dados.ehAnoCorrente && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '16px 18px', marginBottom: '14px' }}>
              <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Projecção — faltam {dados.mesesRestantes} mês(es)
              </p>
              <p style={{ margin: '0 0 12px', fontSize: '11px', color: C.textMuted }}>
                Baseada em <strong>{dados.nMesesFechados} mês(es) fechado(s)</strong>. O mês a decorrer não entra na média — só puxava o valor para baixo.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                <Cenario etiqueta="Pessimista" nota="ao ritmo do pior mês" valor={dados.totalPessimista} cor={C.textMuted} />
                <Cenario etiqueta="Central" nota="ao ritmo da média" valor={dados.totalCentral} cor={C.primary} destaque />
                <Cenario etiqueta="Optimista" nota="ao ritmo do melhor mês" valor={dados.totalOptimista} cor={C.success} />
              </div>
            </div>
          )}

          {/* ── Barra do limite ── */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '16px 18px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Face ao limite de referência
              </span>
              <span style={{ fontSize: '13px', fontWeight: '700', color: corBarra }}>
                {fmt0(dados.totalCentral)} € de {fmt0(limite)} € · {pct.toFixed(0)}%
              </span>
            </div>
            <div style={{ position: 'relative', height: '18px', background: C.bg, borderRadius: '4px', overflow: 'hidden', border: `1px solid ${C.border}` }}>
              <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: `${corBarra}33`, transition: 'width .3s' }} />
              <div style={{ position: 'absolute', top: 0, bottom: 0, width: `${pctRealizado}%`, background: corBarra, transition: 'width .3s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: C.textMuted, marginTop: '4px', fontWeight: '600' }}>
              <span>■ realizado {fmt0(dados.realizado)} €</span>
              <span>▨ projectado até {fmt0(dados.totalCentral)} €</span>
            </div>
          </div>

          {/* Facturação real do Sage — manda nos meses que cobre */}
          <ImportarSage
            ano={ano}
            jaImportado={dados.nMesesSage}
            declarada={declarada}
            plataformaPorMes={Object.fromEntries(dados.porMes.map(m => [m.mes + 1, m.b2b + m.mkt]))}
            onImportado={carregar}
          />

          {/* ── Vendas de marketplace ──
              Vieram do Capital, que foi retirado. É aqui que se lançam as
              facturas das vendas nos marketplaces, e é daqui que a facturação
              acima as vai buscar. */}
          <VendasMarketplace vendas={marketplace} ano={ano} onMudou={carregar} />

          {/* ── Avisos de qualidade dos dados ── */}
          {(dados.mktSemValorFaturado > 0 || dados.b2bSemCompromisso > 0 || dados.nMesesFechados < 3 || dados.mktHistoricasNoAno > 0 || dados.nContratadoEmMesDoSage > 0 || dados.mesesVaziosNoMeio.length > 0) && (
            <div style={{ background: '#fff4ce', border: '1px solid #c0740055', borderRadius: '6px', padding: '12px 16px', marginBottom: '14px', fontSize: '12px', color: '#92400e' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', marginBottom: '6px' }}>
                <AlertTriangle size={14} /> O que este número ainda não sabe
              </div>
              <ul style={{ margin: 0, paddingLeft: '18px', lineHeight: 1.7 }}>
                {dados.nContratadoEmMesDoSage > 0 && (
                  <li>
                    <strong>{fmt(dados.contratadoEmMesDoSage)} €</strong> de “contratado” ({dados.nContratadoEmMesDoSage} encomenda(s)) caem em meses que o <strong>Sage já cobre</strong>.
                    O Sage tem tudo o que foi facturado; a plataforma só sabe o que foi marcado à mão no Financeiro.
                    Se essas facturas já saíram e ninguém as marcou aqui, <strong>estão contadas duas vezes</strong>. Vale a pena confirmar antes de levar este número.
                  </li>
                )}
                {dados.mesesVaziosNoMeio.length > 0 && (
                  <li>
                    {dados.mesesVaziosNoMeio.map(m => MESES[m]).join(', ')} {dados.mesesVaziosNoMeio.length === 1 ? 'está' : 'estão'} a <strong>zero</strong> e {dados.mesesVaziosNoMeio.length === 1 ? 'entra' : 'entram'} na média.
                    Se foi mês sem vendas, está certo. Se são dados por importar, a projecção está a ser puxada para baixo.
                  </li>
                )}
                {dados.nMesesFechados < 3 && (
                  <li>Só há <strong>{dados.nMesesFechados} mês(es) fechado(s)</strong>. A projecção é um indício, não uma previsão.</li>
                )}
                {dados.mktSemValorFaturado > 0 && (
                  <li><strong>{dados.mktSemValorFaturado}</strong> venda(s) de marketplace sem valor facturado preenchido — não entram no total.</li>
                )}
                {dados.b2bSemCompromisso > 0 && (
                  <li>{fmt(dados.b2bSemCompromisso)} € em encomendas sem pagamento nem promoção — ficam de fora por não haver compromisso.</li>
                )}
                {dados.mktHistoricasNoAno > 0 && (
                  <li>
                    <strong>{dados.mktHistoricasNoAno}</strong> venda(s) de marketplace com data de {ano} estão marcadas como <strong>históricas</strong> e por isso <strong>não entram</strong> — são {fmt(dados.mktHistoricasValor)} €.
                    A marca serve para stock anterior ao sistema, mas se a venda aconteceu este ano é receita a declarar. Vale a pena confirmar.
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* ── Por mês ── */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden', marginBottom: '14px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  <th style={th}>Mês</th>
                  <th style={{ ...th, textAlign: 'right' }}>B2B</th>
                  <th style={{ ...th, textAlign: 'right' }}>Marketplaces</th>
                  <th style={{ ...th, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {dados.porMes.map(m => {
                  const vazio = m.total === 0
                  const futuro = dados.ehAnoCorrente && m.mes > new Date().getMonth()
                  return (
                    <tr key={m.mes} style={{ borderTop: `1px solid ${C.border}`, opacity: vazio ? 0.45 : 1 }}>
                      <td style={{ padding: '7px 12px', color: C.text, fontWeight: '600' }}>
                        {MESES[m.mes]}
                        {m.temSage && <span title="Valor vindo do Mapa de vendas do Sage" style={{ marginLeft: '6px', fontSize: '9px', color: C.success, fontWeight: '800' }}>SAGE</span>}
                        {futuro && <span style={{ marginLeft: '6px', fontSize: '9px', color: C.textMuted, fontWeight: '700' }}>POR VIR</span>}
                      </td>
                      <td style={{ padding: '7px 12px', textAlign: 'right', color: C.text }}>{vazio ? '—' : fmt(m.b2b) + ' €'}</td>
                      <td style={{ padding: '7px 12px', textAlign: 'right', color: C.text }}>{m.mkt === 0 ? '—' : fmt(m.mkt) + ' €'}</td>
                      <td style={{ padding: '7px 12px', textAlign: 'right', color: C.text, fontWeight: '700' }}>{vazio ? '—' : fmt(m.total) + ' €'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* ── Resumo copiável ── */}
          <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <Package size={13} color={C.textMuted} />
              <span style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Resumo para o contabilista
              </span>
              <CopyButton text={textoResumo} size={13} title="Copiar resumo" />
            </div>
            <pre style={{ margin: 0, fontSize: '11.5px', fontFamily: 'monospace', color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {textoResumo}
            </pre>
          </div>
        </>
      )}
    </div>
  )
}

const th = { padding: '8px 12px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }

// "Varios" e para o caso normal: o reembolso vem agregado de todos e reparti-lo
// por marketplace seria adivinhar. Para a facturacao chega o total; se um dia
// interessar saber qual vende mais, isso sai dos relatorios deles.
// O que se lanca e SEMPRE sem IVA — e o que e tributado. Mas o numero que ele
// tem a mao e o do reembolso, que vem com IVA, e converter de cabeca todos os
// meses e uma oportunidade de enganar-se. Escreve-se num campo e o outro
// preenche-se sozinho.
const IVA_PADRAO = 23

const MARKETPLACES = ['Vários', 'Kaufland', 'Amazon', 'Vinted', 'eBay', 'OLX', 'Custojusto', 'Outro']

// Registo das vendas de marketplace. Estava no Capital, que foi retirado por
// misturar estimativas com factos. Aqui fica ao lado da facturação que alimenta.
function VendasMarketplace({ vendas, ano, onMudou }) {
  const [aberto, setAberto] = useState(false)
  const [produtos, setProdutos] = useState([])
  const [f, setF] = useState({ id: null, tipo: 'produto', produto: '', qtd: '', valor: '', valorCIva: '', marketplace: MARKETPLACES[0], descricao: '', data: new Date().toISOString().slice(0, 10), recebimento: '', fatura: '', agregada: false })
  const [guardando, setGuardando] = useState(false)
  const [msg, setMsg] = useState('')
  const formRef = useRef(null)

  useEffect(() => {
    if (!aberto || produtos.length) return
    // SO OS ARTIGOS DA CONTA INTERNA.
    //
    // Antes era `from('Produtos').select(...)` sem paginacao. A tabela tem mais
    // de 2.700 linhas e o Supabase corta uma leitura simples nas 1000 —
    // ordenado por nome, tudo o que comecasse depois do "C" desaparecia. O TOM
    // FORD Lost Cherry, que e o artigo que ele mais vende no marketplace, nunca
    // chegava a aparecer na lista.
    //
    // Alem de paginar, filtra-se: o que se vende no marketplace e o stock da
    // conta interna, nao o catalogo todo. Ficam quatro artigos em vez de 2.700,
    // e a lista passa a ser util em vez de exaustiva.
    ;(async () => {
      const { data: perfis } = await supabase.from('perfis').select('email').eq('cliente_interno', true)
      const emails = (perfis || []).map(p => String(p.email || '').toLowerCase()).filter(Boolean)
      if (emails.length === 0) { setProdutos([]); return }
      const { data } = await lerTudo(() => supabase.from('Produtos')
        .select('Nome, EAN, Email_Cliente').order('Nome'), 'Nome')
      const meus = (data || []).filter(p => emails.includes(String(p.Email_Cliente || '').toLowerCase()))
      // Dedup: o mesmo artigo pode existir em mais do que uma conta interna.
      const vistos = new Set()
      setProdutos(meus.filter(p => (vistos.has(p.Nome) ? false : (vistos.add(p.Nome), true))))
    })()
  }, [aberto])

  // A secção fica no fim de uma página longa: ao abrir, o formulário nascia
  // abaixo do que se vê e parecia que o botão não fazia nada. Traz-se à vista.
  useEffect(() => {
    if (aberto && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [aberto])

  const doAno = vendas.filter(v => v.data && new Date(v.data).getFullYear() === Number(ano))
  const total = doAno.filter(v => !v.historica).reduce((s, v) => s + Number(v.valor_faturado ?? v.valor_total ?? 0), 0)

  const guardar = async () => {
    const ehServico = f.tipo === 'servico'
    if (!f.valor) { setMsg('Preenche o valor.'); return }
    // A QUANTIDADE E OPCIONAL.
    //
    // O valor e o que conta para a facturacao, e as unidades que entram no
    // lucro B2C vem dos snapshots de stock — o sistema nunca divide um pelo
    // outro para nada que conte. Num reembolso agregado, com unidades vendidas
    // a 125 e a 175, contar as unidades a mao e trabalho sem retorno.
    if (!ehServico && !f.agregada && !f.produto) { setMsg('Escolhe o artigo, ou marca "vários artigos".'); return }
    setGuardando(true); setMsg('')
    try {
      const prod = (ehServico || f.agregada) ? null : produtos.find(p => p.Nome === f.produto)
      // Custo à data, para o lucro desta venda poder ser calculado depois.
      // Numa linha agregada não se sabe a mistura, por isso fica sem custo em
      // vez de se inventar o do artigo dominante.
      const { data: interno } = (ehServico || f.agregada) ? { data: null } : await supabase.from('produtos_internos')
        .select('preco_custo').eq('produto_nome', f.produto).maybeSingle()
      const valorNum = Number(f.valor)
      const dados = {
        tipo: f.tipo,
        // Num servico nao ha artigo nem unidades. Guarda-se a descricao no
        // campo do nome para a linha nao ficar anonima na lista.
        produto_nome: ehServico ? (f.descricao.trim() || 'Serviços prestados') : (f.agregada ? null : f.produto),
        produto_ean: prod?.EAN ? String(prod.EAN) : null,
        qtd: (ehServico || f.qtd === '') ? null : Number(f.qtd),
        // DOIS NUMEROS, DUAS PERGUNTAS.
        //   valor_faturado = sem IVA -> facturacao, que e o que e tributado
        //   valor_total    = com IVA -> tesouraria, que e o que entra na conta
        // Entravam os dois com o valor sem IVA e a projeccao ficava a menos:
        // 680,00 onde entram 836,40. A Tesouraria e sobre dinheiro.
        valor_total: f.valorCIva !== '' ? Number(f.valorCIva)
                   : Math.round(valorNum * (1 + IVA_PADRAO / 100) * 100) / 100,
        valor_faturado: valorNum,
        custo_unitario_no_momento: interno?.preco_custo != null ? Number(interno.preco_custo) : null,
        // A coluna e obrigatoria na base e um servico nao tem marketplace
        // nenhum. Grava-se a etiqueta em vez de NULL: evita mais uma migracao,
        // e na lista le-se melhor do que uma celula vazia.
        marketplace: ehServico ? 'Serviços' : f.marketplace,
        data: f.data,
        // DUAS DATAS, porque sao duas coisas.
        //
        // `data` e a da factura — decide o mes e o ano da facturacao, que e o
        // que o fisco olha. `data_recebimento` e quando o dinheiro cai na
        // conta, ~20 dias depois, e e essa que a Tesouraria usa. Uma venda de
        // 20 de Dezembro e facturacao de Dezembro e dinheiro de Janeiro; com
        // uma data so, uma das duas contas ficava errada.
        data_recebimento: f.recebimento || null,
        fatura_numero: f.fatura.trim() || null,
        historica: false,
      }
      // O MESMO FORMULARIO CRIA E EDITA. A data da factura so se sabe no dia em
      // que se pede o reembolso ao marketplace, e antes disto uma linha lancada
      // com a data errada so se corrigia apagando e voltando a escrever.
      const { error } = f.id
        ? await supabase.from('vendas_marketplace').update(dados).eq('id', f.id)
        : await supabase.from('vendas_marketplace').insert(dados)
      if (error) throw new Error(error.message)
      // Mantém nº da factura, marketplace e data: a seguir vem quase sempre
      // outro artigo da MESMA factura, e reescrevê-los de cada vez era trabalho
      // repetido e uma oportunidade de enganar-se.
      if (f.id) {
        setF({ ...f, id: null, produto: '', qtd: '', valor: '', valorCIva: '', descricao: '' })
        setMsg('Alterações guardadas.')
      } else {
        // Mantem no da factura, marketplace e data: a seguir vem quase sempre
        // outro artigo da MESMA factura.
        setF({ ...f, produto: '', qtd: '', valor: '', valorCIva: '' })
        setMsg(`Registado. Continua na factura ${f.fatura || '(sem nº)'} — lança o artigo seguinte.`)
      }
      await onMudou()
    } catch (e) { setMsg('Erro: ' + e.message) }
    setGuardando(false)
  }

  const editar = (v) => {
    setMsg('')
    setAberto(true)
    setF({
      id: v.id,
      tipo: v.tipo || 'produto',
      produto: v.produto_nome || '',
      descricao: v.tipo === 'servico' ? (v.produto_nome || '') : '',
      qtd: v.qtd == null ? '' : String(v.qtd),
      valor: String(v.valor_faturado ?? v.valor_total ?? ''),
      valorCIva: String(Math.round(Number(v.valor_faturado ?? v.valor_total ?? 0) * (1 + IVA_PADRAO / 100) * 100) / 100),
      marketplace: v.marketplace || MARKETPLACES[0],
      data: String(v.data || '').slice(0, 10),
      recebimento: v.data_recebimento ? String(v.data_recebimento).slice(0, 10) : '',
      fatura: v.fatura_numero || '',
      agregada: v.tipo !== 'servico' && !v.produto_nome,
    })
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50)
  }

  // MARCAR COMO RECEBIDO.
  //
  // Pergunta data E valor, ja preenchidos com o esperado, porque na pratica
  // nenhum dos dois e garantido: combina-se dia 7 e entra dia 8, espera-se 700
  // e vem 750 com um bonus. Corrigir aqui e mais rapido do que ir ao lapis.
  const marcarRecebido = async (v) => {
    const esperado = Number(v.valor_total ?? v.valor_faturado ?? 0)
    const dataStr = window.prompt(
      'Em que dia entrou o dinheiro? (AAAA-MM-DD)',
      v.data_recebimento ? String(v.data_recebimento).slice(0, 10) : new Date().toISOString().slice(0, 10))
    if (!dataStr) return
    const valStr = window.prompt(
      'Quanto entrou, com IVA? (o que a conta recebeu)', esperado.toFixed(2))
    if (!valStr) return
    const val = Number(String(valStr).replace(',', '.'))
    if (!(val > 0)) { setMsg('Valor inválido.'); return }
    const { error } = await supabase.from('vendas_marketplace')
      .update({ recebido_em: dataStr, valor_total: Math.round(val * 100) / 100 })
      .eq('id', v.id)
    if (error) { setMsg('Erro: ' + error.message); return }
    await onMudou()
  }

  const desmarcarRecebido = async (id) => {
    await supabase.from('vendas_marketplace').update({ recebido_em: null }).eq('id', id)
    await onMudou()
  }

  const apagar = async (id) => {
    if (!window.confirm('Apagar este registo de venda?')) return
    await supabase.from('vendas_marketplace').delete().eq('id', id)
    await onMudou()
  }

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', marginBottom: '14px', overflow: 'hidden' }}>
      {/* Cabeçalho com fundo próprio: antes era uma linha fina no meio de
          cartões todos iguais e passava despercebida. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', padding: '12px 16px', background: C.bg, borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: '13px', fontWeight: '800', color: C.text, display: 'flex', alignItems: 'center', gap: '7px' }}>
          <Store size={15} color={C.primary} />
          Vendas de marketplace {ano}
          <span style={{ color: C.primary, fontWeight: '800' }}>{fmt(total)} €</span>
          <span style={{ color: C.textMuted, fontWeight: '600', fontSize: '11px' }}>
            · {doAno.filter(v => !v.historica).length} venda(s)
          </span>
        </span>
        <button onClick={() => setAberto(a => !a)}
          style={{ padding: '7px 14px', border: 'none', borderRadius: '4px', background: aberto ? C.surfaceAlt : C.primary, color: aberto ? C.text : '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
          {aberto ? 'Fechar' : <><Plus size={13} /> Registar venda ou serviço</>}
        </button>
      </div>

      {aberto && (
        <div ref={formRef} style={{ margin: '12px 16px', padding: '12px', background: C.surfaceAlt, border: `2px solid ${C.primary}`, borderRadius: '4px' }}>
          {/* Lançamento agregado: para a facturação anual o que conta é o total,
              e o detalhe por artigo já vai na factura para a contabilidade. */}
          {/* O QUE SE ESTA A LANCAR. Sao dois factos com o mesmo destino: os
              dois somam para a facturacao, mas um tem custo de mercadoria e o
              outro e todo margem — e o regime fiscal trata-os de forma
              diferente. Por isso separam-se a entrada, e nao depois. */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
            {[
              { id: 'produto', l: 'Venda de mercadoria' },
              { id: 'servico', l: 'Serviço prestado' },
            ].map(x => (
              <button key={x.id} type="button" onClick={() => setF({ ...f, tipo: x.id, agregada: false })}
                style={{
                  padding: '5px 12px', borderRadius: '3px', cursor: 'pointer', fontSize: '12px',
                  fontWeight: f.tipo === x.id ? '800' : '600',
                  border: `1px solid ${f.tipo === x.id ? C.primary : C.border}`,
                  background: f.tipo === x.id ? C.primary : C.surface,
                  color: f.tipo === x.id ? '#fff' : C.textMuted,
                }}>
                {x.l}
              </button>
            ))}
          </div>

          {f.tipo === 'produto' && <label style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', margin: '0 0 10px', padding: '8px 10px', background: f.agregada ? '#fff4ce' : C.surface, border: `1px solid ${f.agregada ? '#c0740055' : C.border}`, borderRadius: '4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={f.agregada} onChange={e => setF({ ...f, agregada: e.target.checked, produto: '' })}
              style={{ cursor: 'pointer', accentColor: C.primary, marginTop: '2px' }} />
            <span style={{ fontSize: '11.5px', color: C.text, lineHeight: 1.5 }}>
              <strong>Factura com vários artigos</strong> — lançar numa linha só
              <div style={{ color: C.textMuted, fontSize: '10.5px', marginTop: '2px' }}>
                Para a facturação chega o total. O detalhe por artigo vai na factura para a contabilidade.
                Fica sem custo associado — não se atribui a mistura a um artigo só, que daria um lucro errado com ar de certo.
              </div>
            </span>
          </label>}
          {!f.agregada && f.tipo === 'produto' && (
            <p style={{ margin: '0 0 10px', fontSize: '11px', color: C.textMuted, lineHeight: 1.6 }}>
              Uma linha por <strong>artigo</strong>, repetindo o nº da factura — assim há lucro por artigo.
            </p>
          )}
          {f.tipo === 'servico' && (
            <p style={{ margin: '0 0 10px', fontSize: '11px', color: C.textMuted, lineHeight: 1.6 }}>
              Um serviço não tem artigo nem unidades, e não tem custo de mercadoria — o valor facturado é todo margem.
            </p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
            {f.tipo === 'servico' && (
              <Campo label="Descrição">
                <input value={f.descricao} onChange={e => setF({ ...f, descricao: e.target.value })}
                  style={inp} placeholder="Consultoria de marketplace" />
              </Campo>
            )}
            {f.tipo === 'produto' && <Campo label={f.agregada ? 'Artigo (vários — não se indica)' : 'Produto'}>
              <input list="lista-produtos-mkt" value={f.agregada ? '' : f.produto} disabled={f.agregada}
                onChange={e => setF({ ...f, produto: e.target.value })}
                style={{ ...inp, background: f.agregada ? C.bg : undefined, color: f.agregada ? C.textMuted : undefined }}
                placeholder={f.agregada ? 'vários artigos nesta factura' : 'nome do produto'} />
              <datalist id="lista-produtos-mkt">{produtos.map(p => <option key={p.Nome} value={p.Nome} />)}</datalist>
            </Campo>}
            {f.tipo === 'produto' && <Campo label="Quantidade (opcional)">
              <input type="number" min="1" value={f.qtd} onChange={e => setF({ ...f, qtd: e.target.value })} style={inp} placeholder="deixa em branco" />
              <div style={{ fontSize: '10px', color: C.textFaint, marginTop: '2px' }}>as unidades saem do stock</div>
            </Campo>}
            {/* É o TOTAL da linha, não o preço unitário. Na mesma factura o mesmo
                artigo sai a preços diferentes; pedir preço unitário obrigaria a
                inventar uma média. Assim lança-se o que a factura diz. */}
            <Campo label={`TOTAL C/IVA (€) · ${IVA_PADRAO}%`}>
              <input type="number" min="0" step="0.01" value={f.valorCIva}
                onChange={e => {
                  const v = e.target.value
                  setF({ ...f, valorCIva: v, valor: v === '' ? '' : String(Math.round((Number(v) / (1 + IVA_PADRAO / 100)) * 100) / 100) })
                }}
                style={inp} placeholder="o valor do reembolso" />
              <div style={{ fontSize: '10px', color: C.textFaint, marginTop: '2px' }}>escreve aqui o que recebeste</div>
            </Campo>
            <Campo label="TOTAL facturado S/IVA (€)">
              <input type="number" min="0" step="0.01" value={f.valor}
                onChange={e => {
                  const v = e.target.value
                  setF({ ...f, valor: v, valorCIva: v === '' ? '' : String(Math.round(Number(v) * (1 + IVA_PADRAO / 100) * 100) / 100) })
                }}
                style={{ ...inp, fontWeight: '700' }}
                placeholder="total da linha, não preço/un"
                title="TOTAL desta linha da factura, sem IVA e já líquido de comissões. E este o valor que fica gravado — e o que e tributado." />
              <div style={{ fontSize: '10px', color: C.textFaint, marginTop: '2px' }}>é este que fica gravado</div>
            </Campo>
            {f.tipo === 'produto' && <Campo label="Marketplace">
              <select value={f.marketplace} onChange={e => setF({ ...f, marketplace: e.target.value })} style={inp}>
                {MARKETPLACES.map(m => <option key={m}>{m}</option>)}
              </select>
            </Campo>}
            <Campo label="Data da factura"><input type="date" value={f.data} onChange={e => setF({ ...f, data: e.target.value })} style={inp} /></Campo>
            <Campo label="Dinheiro entra a">
              <input type="date" value={f.recebimento} onChange={e => setF({ ...f, recebimento: e.target.value })} style={inp} />
              <div style={{ fontSize: '10px', color: C.textFaint, marginTop: '2px' }}>vazio = 20 dias depois</div>
            </Campo>
            <Campo label="Nº factura (opcional)"><input value={f.fatura} onChange={e => setF({ ...f, fatura: e.target.value })} style={inp} placeholder="FT 2026/…" /></Campo>
          </div>
          {f.valor !== '' && Number(f.valor) > 0 && (
            <div style={{ fontSize: '10.5px', color: C.textMuted, marginTop: '6px', lineHeight: 1.6 }}>
              {/* Média implícita — serve de conferência, não é o que se lança */}
              {Number(f.qtd) > 0 && (
                <>Dá uma média de <strong style={{ color: C.text }}>{(Number(f.valor) / Number(f.qtd)).toFixed(2)} €/un</strong>.
                  Se as unidades saíram a preços diferentes está bem assim — o que conta é o total.<br /></>
              )}
              Grava-se o valor <strong>sem IVA</strong> — é o que é tributado. Se a taxa não for {IVA_PADRAO}%,
              escreve directamente no campo s/IVA e ignora o outro.
            </div>
          )}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '8px' }}>
            {f.id && (
              <div style={{ gridColumn: '1 / -1', marginBottom: '8px', fontSize: '11.5px', fontWeight: '700', color: C.primary }}>
                A editar um registo que já existe — Guardar actualiza-o.{' '}
                <button type="button" onClick={() => setF({ ...f, id: null, produto: '', qtd: '', valor: '', descricao: '' })}
                  style={{ background: 'none', border: 'none', color: C.textMuted, textDecoration: 'underline', cursor: 'pointer', fontSize: '11.5px' }}>
                  cancelar edição
                </button>
              </div>
            )}
            <button onClick={guardar} disabled={guardando}
              style={{ padding: '6px 14px', border: 'none', borderRadius: '4px', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: '11.5px', fontWeight: '700' }}>
              {guardando ? 'A guardar…' : 'Guardar'}
            </button>
            {msg && <span style={{ fontSize: '11px', color: msg.startsWith('Erro') ? C.danger : C.success, fontWeight: '600' }}>{msg}</span>}
          </div>
        </div>
      )}

      {doAno.length === 0 ? (
        <p style={{ margin: 0, padding: '14px 16px', fontSize: '12px', color: C.textMuted, fontStyle: 'italic' }}>
          Nenhuma venda de marketplace registada em {ano}. Usa o botão acima para lançar as facturas.
        </p>
      ) : (
        <>
        {/* Altura generosa: com poucas vendas mostra tudo sem scroll interno
            (dois scrolls encaixados são incómodos), e só trava quando a lista
            cresce ao ponto de dominar a página. */}
        <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr style={{ background: C.surfaceAlt }}>
              <th style={thMkt}>Artigo</th>
              <th style={thMkt}>Qtd · Marketplace</th>
              <th style={thMkt}>Nº factura</th>
              <th style={thMkt}>Data</th>
              <th style={{ ...thMkt, textAlign: 'right' }}>Valor s/IVA</th>
              <th style={{ ...thMkt, width: '30px' }}></th>
            </tr>
          </thead>
          <tbody>
            {doAno.map(v => (
              <tr key={v.id} style={{ borderTop: `1px solid ${C.border}`, background: v.historica ? '#faf9ff' : 'transparent' }}>
                <td style={{ padding: '7px 16px', color: C.text, fontWeight: '600' }}>
                  {v.produto_nome || <em style={{ color: C.textMuted, fontWeight: '400' }}>vários artigos</em>}
                  {v.historica && (
                    <span title="Registo anterior ao sistema — não entra na facturação acima"
                      style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#5b21b6', background: '#ede9fe', border: '1px solid #5b21b644', borderRadius: 8, padding: '1px 6px' }}>HISTÓRICA</span>
                  )}
                </td>
                <td style={{ padding: '7px 8px', color: C.textMuted, whiteSpace: 'nowrap' }}>
                  {v.qtd != null && `${v.qtd} un`}{v.qtd != null && v.marketplace ? ' · ' : ''}{v.marketplace || (v.tipo === 'servico' ? 'serviço' : '')}
                </td>
                <td style={{ padding: '7px 8px', color: v.fatura_numero ? C.text : C.textMuted, whiteSpace: 'nowrap' }}>
                  {v.fatura_numero || <span style={{ fontStyle: 'italic' }}>sem nº</span>}
                </td>
                <td style={{ padding: '7px 8px', color: C.textMuted, whiteSpace: 'nowrap' }}>{v.data ? new Date(v.data).toLocaleDateString('pt-PT') : '—'}</td>
                <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: '700', color: C.text, whiteSpace: 'nowrap' }}>{fmt(v.valor_faturado ?? v.valor_total)} €</td>
                <td style={{ padding: '7px 12px 7px 4px', textAlign: 'right' }}>
                  {v.recebido_em ? (
                    <button onClick={() => desmarcarRecebido(v.id)}
                      title={`Recebido a ${new Date(v.recebido_em).toLocaleDateString('pt-PT')} · carrega para desmarcar`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.success, fontSize: '13px', fontWeight: '800', lineHeight: 1, marginRight: '4px' }}>✓</button>
                  ) : (
                    <button onClick={() => marcarRecebido(v)} title="Marcar como recebido"
                      style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: '3px', cursor: 'pointer', color: C.textMuted, fontSize: '10px', fontWeight: '700', lineHeight: 1, padding: '2px 5px', marginRight: '4px' }}>recebido?</button>
                  )}
                  <button onClick={() => editar(v)} title="Editar este registo"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: '12px', lineHeight: 1, marginRight: '4px' }}>✎</button>
                  <button onClick={() => apagar(v.id)} title="Apagar este registo"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: '15px', lineHeight: 1 }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {/* Total fora da zona de scroll, para nunca desaparecer de vista */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '9px 16px', borderTop: `2px solid ${C.border}`, background: C.surfaceAlt }}>
          <span style={{ fontWeight: '800', color: C.text, fontSize: '12px' }}>
            Total {ano} <span style={{ fontWeight: '400', color: C.textMuted, fontSize: '11px' }}>(sem as históricas)</span>
          </span>
          <span style={{ fontWeight: '800', color: C.primary, fontSize: '14px' }}>{fmt(total)} €</span>
        </div>
        </>
      )}
    </div>
  )
}

const thMkt = { padding: '7px 16px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.4px' }

const inp = { width: '100%', padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', boxSizing: 'border-box' }
function Campo({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: C.textMuted, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</span>
      {children}
    </label>
  )
}

function Camada({ cor, etiqueta, nota, valor, detalhe, destaque }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', padding: '6px 0', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0 }}>
        <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: cor, flexShrink: 0 }} />
        <span style={{ fontSize: destaque ? '14px' : '13px', fontWeight: destaque ? '800' : '600', color: C.text }}>{etiqueta}</span>
        {nota && <span style={{ fontSize: '11px', color: C.textMuted }}>{nota}</span>}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: destaque ? '20px' : '15px', fontWeight: destaque ? '800' : '700', color: destaque ? C.text : cor }}>
          {fmt(valor)} €
        </div>
        {detalhe && <div style={{ fontSize: '10px', color: C.textMuted }}>{detalhe}</div>}
      </div>
    </div>
  )
}

function Cenario({ etiqueta, nota, valor, cor, destaque }) {
  return (
    <div style={{ padding: '12px 14px', border: `${destaque ? 2 : 1}px solid ${cor}${destaque ? '' : '44'}`, borderRadius: '4px', background: destaque ? `${cor}0d` : 'transparent' }}>
      <div style={{ fontSize: '10px', fontWeight: '700', color: cor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{etiqueta}</div>
      <div style={{ fontSize: '19px', fontWeight: '800', color: cor, margin: '2px 0' }}>{fmt0(valor)} €</div>
      <div style={{ fontSize: '10px', color: C.textMuted }}>{nota}</div>
    </div>
  )
}
